import { blue, cyan, deepOrange, green, orange, purple, teal } from "@mui/material/colors";
import { SxProps } from "@mui/material/styles";
import {
  MRT_ColumnDef as ColumnDef,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_TableInstance as TableInstance,
} from "material-react-table";
import Permission from "permissions/permission";
import { PermissionParams } from "permissions/_schemas/permission-schema";
import PermissionTemplate from "permissions/templates/permission-template";
import { PermissionTemplateParams } from "permissions/templates/_schemas/permission-template-schema";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import { PermissionsByGranteeParams } from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";
import { LegalEntityAccessType, LegalEntitiesPlatformAccessType } from "permissions/resources/legal-entities/types";
import type { Resource } from "permissions/resources/permission-resource";
import { ChildResourceType, ResourceConstructor, ResourceType } from "permissions/resources/types";
import { LoginAccessType } from "permissions/resources/users/types";
import React, { JSX, ReactNode } from "react";

export const CATEGORY_OPTIONS = [
  {
    label: "Communication",
    regex: /(Forum|Communication|Chat|Network|Notification)/,
    color: cyan[800],
  },
  {
    label: "Financial",
    regex: /Fee|Discount|Invoice|Currency/,
    color: green[800],
  },
  {
    label: "Work",
    regex: /Proposal|Work|Team/,
    color: orange[800],
  },
  {
    label: "Documents",
    regex: /(Document|Contract)/,
    color: deepOrange[800],
  },
  {
    label: "Marketplace",
    regex: /Marketplace/,
    color: purple[800],
  },
  {
    label: "Account",
    regex: /(Login|Entity)/,
    color: teal[800],
  },
  {
    label: "Knowledge Base",
    regex: /KnowledgeBase/,
    color: blue[800],
  },
] as const;

export type CategoryLabel = (typeof CATEGORY_OPTIONS)[number]["label"];

export type ChildResourceConstructorMap = Record<keyof typeof ChildResourceType, ResourceConstructor>;

export type TopLevelTemplatesIndex = {
  byConstructor: Map<ResourceConstructor, Resource>;
  legalByAccessType: Map<string, Resource>;
  docContentsBySubject: Map<string, Resource>;
};

export type PermissionFilterParameters = {
  granteeUserId?: string;
  granteeUserIds?: string[]; // multi-select
  grantorUserId?: string;
  resource?: string;
  minCreatedDate?: string;
  maxCreatedDate?: string;
  minDisabledDate?: string;
  maxDisabledDate?: string;
  categories?: string[];
  accessType?: string;
  childAccessTypes?: string[];
  hasParent?: "true" | "false";
};

export type AccessTypes = LegalEntityAccessType | LegalEntitiesPlatformAccessType | LoginAccessType;

export const AccessTypeEnumByResourceType = {
  [ResourceType.LegalEntityAccess]: LegalEntityAccessType,
  [ResourceType.LegalEntitiesPlatformAccess]: LegalEntitiesPlatformAccessType,
  [ResourceType.LoginAccess]: LoginAccessType,
};

export type ColumnFilter = ColumnFiltersState[number];

export type CardMeta = {
  key: string;
  permission?: Permission;
  resource: Resource;
  isVirtual?: boolean;
  control: ControlMeta | null;
};

export type ControlMeta = {
  type: MetaControlType;
  label: "Access Type" | "Scope" | "Access Level";
  options: string[];
  value: string;
};

export type SortedColumnDef = {
  sortKey: string;
  columnDefinition: ColumnDef<TableRow>;
};

export type SingleCellConfig = {
  cardType: CardType.Single;
  resource: ResourceConstructor;
  candidates: (row: TableRow, resource: ResourceConstructor) => Permission[] | Resource[];
  template?: (resource: ResourceConstructor) => Resource | undefined;
  title?: (args: { resource: ResourceConstructor; permission: Permission }) => string | undefined;
  colSpan?: number;
  rowSpan?: number;
  canEdit: boolean;
};

export type GroupedCellConfig = {
  cardType: CardType.Grouped;
  keys: string[];
  templateForKey?: (key: string) => Resource | undefined;
  candidatesForKey: (row: TableRow, key: string) => Permission[] | Resource[];
  title: string | undefined;
  labelForKey: (key: string) => React.ReactNode;
  controlMetaForResource: (r: Resource, key: string) => ControlMeta | null;
  colSpan?: number;
  rowSpan?: number;
  canEdit: boolean;
};

export type UniversalCellConfig = SingleCellConfig | GroupedCellConfig;

export type CardStyle = {
  baseColor: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  display: "none" | "flex";
  selectSx: SxProps;
};

export type PermissionProps = {
  renderTopToolbarCustomActions: (
    props: {
      table: TableInstance<TableRow>;
    },
    additional?: JSX.Element,
    filters?: JSX.Element
  ) => ReactNode;
};

export enum CardType {
  Single,
  Grouped,
}

export enum MetaControlType {
  AccessLevel,
  Scope,
  AccessType,
}

export type TableRow = PermissionsByGrantee | PermissionTemplate;

export type EditableFields = {
  name: string;
  description?: string;
  resources: Resource[];
};

export type PermissionCtorParams = Omit<PermissionParams, "resource" | "parentPermissions"> & {
  resource: Resource;
  parentPermissions?: Permission[];
};

export type PermissionTemplateCtorParams = Omit<PermissionTemplateParams, "resources"> & {
  resources: Resource[];
};

export type PermissionsByGranteeCtorParams = Omit<PermissionsByGranteeParams, "recentPermissionsPreview"> & {
  recentPermissionsPreview: Permission[];
};
